/** @jsxImportSource @emotion/react */
import type { MouseEvent } from 'react';
import { useMemo, useContext, useCallback, memo, useState } from 'react';
import { ArrowDown, ArrowUp, ExternalLink, Filter } from 'react-feather';
import InfiniteScroll from 'react-infinite-scroll-component';
import styled from '@emotion/styled';
import { useFloating, useInteractions, useDismiss, autoUpdate } from '@floating-ui/react';
import { createId } from '@paralleldrive/cuid2';
import type { KolSearchResult, KolSearchRecommendation } from '@trinity/chat';
import { TextQueryHint } from '@trinity/chat-components/components/presentation/Message.QueryHint/index.js';
import { TableFilterTooltip } from '@trinity/chat-components/components/presentation/Table.FilterTooltip';
import { Tooltip } from '@trinity/chat-components/components/presentation/Tooltip';
import { colors } from '@trinity/chat-components/components/Theme/theme';
import { AppThemingContext } from '@trinity/chat-components/containers/AppTheming/Context';
import { useIsDemoEnv } from '@trinity/chat-components/containers/Chat';
import { useHasSmartRouting } from '@trinity/chat-components/containers/Chat/selectors';
import { useActiveBrandIdentifier } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import { useTablePaginationHelpers } from '@trinity/chat-components/hooks/useTablePagination';
import { buildKolMailTo, createOneTapLink, formatPhoneNumber  } from '@trinity/chat-components/utils';
import { ChatStateDispatchContext } from '../Chat.State/context';
import { Checkbox } from '../presentation/Checkbox';
import { TablePagination } from '../presentation/TablePagination';
import {
  KolSearchResultsSortContext,
  KolSearchResultsFilterContext,
  KolSearchResultsSelectionContext,
  KolSearchResultsPageSizeContext,
  KolSearchResultsPageIndexContext,
  KolSearchResultsSetPageIndexContext,
  KolSearchTablePageContext,
  KolSearchFormContext,
  FetchMoreResultsContext,
  KolSearchIdentifierContext,
  ActiveChatSessionContext,
  KolSearchRecommendationsContext,
  SmartRoutingHcpContext,
} from './context';
import { useIsSmartRouting } from './hooks/useIsSmartRouting';
import { useKolSearch } from './hooks/useKolSearch';
import type { KolSearchResultSortKey, SortFnMap, StringAccessorFnMap } from './interfaces';
import { KolAddress } from './KolSearch.Address';
import { KolBadgeTooltip } from './KolSearch.KolBadge';
import { KolRecommendationTooltip, NonMunRecommendationTooltip } from './KolSearch.Recommended';
import { FilterPanel } from './KolSearch.Table.Filters';
import { areFiltersSimilar, isFilterValid } from './utils';

type TableProps = {
  className?: string;
  onSelectProfile: (kol: Pick<KolSearchRecommendation, 'id' | 'name'>) => void;
  isInteractive?: boolean;
  infiniteScroll?: boolean;
  smartRoutingDisabled?: boolean;
};

type ChildrenProps = {
  children: React.ReactNode;
};

type ContainerProps = {
  records: KolSearchRecommendation[];
} & ChildrenProps;

export const KolResultsContainer = (props: ContainerProps) => {
  const sortState = useContext(KolSearchResultsSortContext);
  const filterContext = useContext(KolSearchResultsFilterContext);
  const filterState = useMemo(() => {
    return filterContext?.state || [];
  }, [filterContext?.state]);

  const brandIdentifier = useActiveBrandIdentifier();
  const searchIdentifier = useContext(KolSearchIdentifierContext);
  const chat = useContext(ActiveChatSessionContext);
  const recommendations = useContext(KolSearchRecommendationsContext);
  const originalForm = useContext(KolSearchFormContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const smartRoutingContext = useContext(SmartRoutingHcpContext);
  const [requery, setRequery] = useState(false);
  const stableSearchIdentifier = useMemo(() => {
    return searchIdentifier || createId();
  }, [searchIdentifier]);

  const formWithLocation = useMemo(() => {
    if (smartRoutingContext?.startingLocation) {
      const { startingLocation } = smartRoutingContext;

      let locationId: string | null;
      let locationName: string | null;

      if (startingLocation.type === 'hcp') {
        locationId = String(startingLocation.id);
        locationName = startingLocation.name;
      } else if (startingLocation.type === 'browser' || startingLocation.type === 'search') {
        locationId = null;
        locationName = null;
      } else {
        return originalForm;
      }

      return {
        ...originalForm,
        location: {
          id: locationId,
          latitude: startingLocation.latitude,
          longitude: startingLocation.longitude,
          name: locationName,
        },
      };
    }
    return originalForm;
  }, [originalForm, smartRoutingContext]);

  const hasFiltersApplied = useMemo(() => {
    const validFiltersCount = filterState.filter(f => isFilterValid(f)).length;
    if (!requery) setRequery(validFiltersCount > 0);
    return validFiltersCount > 0;
  }, [filterState, requery]);

  const hasSortApplied = useMemo(() => {
    if (!requery) setRequery(sortState && sortState.sortBy != null && sortState.sortDir != null);
    return sortState && sortState.sortBy != null && sortState.sortDir != null;
  }, [sortState, requery]);

  const shouldTriggerQuery = hasFiltersApplied || hasSortApplied;

  const searchParams = useMemo(() => ({
    chatIdentifier: chat.identifier,
    identifier: stableSearchIdentifier,
    form: formWithLocation || { name: '' },
    recommendations: recommendations,
    brandIdentifier: brandIdentifier,
    filters: filterState,
    sort: sortState,
    enabled: shouldTriggerQuery || !!searchIdentifier, // Enable if we have filters/sorts OR original search
    dispatch: dispatch,
  }), [
    chat.identifier,
    stableSearchIdentifier,
    formWithLocation,
    recommendations,
    brandIdentifier,
    filterState,
    sortState,
    shouldTriggerQuery,
    searchIdentifier,
    dispatch,
  ]);

  const searchResult = useKolSearch(searchParams);

  const records = useMemo(() => {
    if (shouldTriggerQuery) {
      return searchResult.data?.result?.results || [];
    }
    return props.records || [];
  }, [shouldTriggerQuery, searchResult.data, props.records]);

  const pageSize = useContext(KolSearchResultsPageSizeContext);
  const pageIndex = useContext(KolSearchResultsPageIndexContext);

  const page = useMemo(() => {
    const start = pageIndex * pageSize;
    const end = start + pageSize;

    return records.slice(start, end);
  }, [
    records,
    pageIndex,
    pageSize,
  ]);

  const hasSmartRouting = useHasSmartRouting();

  const showDistanceCol = useMemo(() => {
    const hasLocation = formWithLocation?.location != null;
    return hasSmartRouting && (hasLocation || records.some(r => typeof r.distance === 'number'));
  }, [hasSmartRouting, formWithLocation?.location, records]);

  const ctx = useMemo(() => ({
    records: page,
    total: records.length,
    showDistanceCol,
    requery,
  }), [page, records.length, showDistanceCol, requery]);

  return (
    <KolSearchTablePageContext.Provider value={ctx}>
      {props.children}
    </KolSearchTablePageContext.Provider>
  );
};

export const KolResultsTable = ({
  className,
  onSelectProfile,
  isInteractive,
  infiniteScroll = false,
  smartRoutingDisabled,
}: TableProps) => {
  const pageSize = useContext(KolSearchResultsPageSizeContext);
  const pageIndex = useContext(KolSearchResultsPageIndexContext);
  const setPageIndex = useContext(KolSearchResultsSetPageIndexContext);

  const isSmartRouting = useIsSmartRouting();

  const allowSelection = useMemo(() => isSmartRouting && !smartRoutingDisabled, [isSmartRouting, smartRoutingDisabled]);
  const showViewProfile = useMemo(() => smartRoutingDisabled != false, [smartRoutingDisabled]);

  const {
    showDistanceCol,
    records,
    total,
  } = useContext(KolSearchTablePageContext);

  const columnWidths = useMemo(() => [allowSelection ? 60 : null, 150, showViewProfile ? 75 : null, 190, 190, 175, 150].filter(Boolean), [allowSelection, showViewProfile]);

  const {
    state: filterState,
    open: filtersOpen,
    setOpen: setFiltersOpen,
  } = useContext(KolSearchResultsFilterContext);

  const sortState = useContext(KolSearchResultsSortContext);
  const { palette, theme: wl } = useContext(AppThemingContext);

  const headerFilterStrokeColor = useMemo(() => {
    return wl?.enabled && palette?.primary?.main ? palette.primary.main : colors.blue.main;
  }, [wl?.enabled, palette?.primary?.main]);

  const {
    selected: selectedKols,
    toggle: toggleKolSelection,
  } = useContext(KolSearchResultsSelectionContext);
  const { refs, floatingStyles, context } = useFloating({
    open: filtersOpen,
    onOpenChange: setFiltersOpen,
    placement: 'top-start',
    whileElementsMounted: autoUpdate,
  });
  const dismiss = useDismiss(context);
  const { getReferenceProps, getFloatingProps } = useInteractions([dismiss]);
  const hasActiveFilters = useMemo(() => {
    const hasFilters = filterState.some(f => isFilterValid(f));
    const hasSort = sortState && Object.keys(sortState).length > 0;
    return hasFilters || hasSort;
  }, [filterState, sortState]);

  const showFiltersInHeader = useMemo(() => {
    return hasActiveFilters && !isInteractive;
  }, [hasActiveFilters, isInteractive]);

  const filterPanelTopOffset = useMemo(() => {
    switch (filterState.length) {
      case 0:
        return '0rem';
      case 1:
        return '6rem';
      case 2:
        return '9rem';
      case 3:
        return '14.2rem';
      default:
        return '0rem';
    }
  }, [filterState]);

  const isDemoEnv = useIsDemoEnv();

  const rows = useMemo(() => {
    return records.map((result, index) => {
      const primaryAfilliation = result.affiliations[0];
      const unmetNeeds = isDemoEnv ? [] : (result.recommendations || []).filter(f => f.unmetNeeds?.length > 0);
      const nonUmnetNeeds = (result.recommendations || []).filter(f => f.unmetNeeds?.length === 0);

      return (
        <FlexibleItemContainer key={index} columns={columnWidths}>
          {allowSelection &&
            <CheckboxAttributeContainer>
              <SelectAttribute>
                <Checkbox
                  checked={selectedKols.includes(result.id)}
                  onChange={() => toggleKolSelection(result.id)}
                />
              </SelectAttribute>
            </CheckboxAttributeContainer>
          }
          <NameAttributeContainer>
            <NameAttribute>
              {result.name}
              <Badges>
                {result.isKol && <KolBadgeTooltip />}
                {!!unmetNeeds.length && <KolRecommendationTooltip values={unmetNeeds} />}
                {!!nonUmnetNeeds.length && <NonMunRecommendationTooltip values={nonUmnetNeeds} />}
              </Badges>
            </NameAttribute>
          </NameAttributeContainer>
          {showViewProfile && (
            <ViewProfileAttributeContainer>
              <ViewProfileAttribute>
                <StyledViewProfileButton
                  disabled={!isInteractive}
                  onClick={() => onSelectProfile(result)}
                  value="View" />
              </ViewProfileAttribute>
            </ViewProfileAttributeContainer>
          )}
          <AffiliationAttributeContainer>
            <AffiliationAttribute>
              {primaryAfilliation ? (
                <Tooltip
                  title={primaryAfilliation.name}
                  enterDelay={1000}
                  enterNextDelay={500}>
                  <div css={{ overflow: 'hidden' }}>{primaryAfilliation.name}</div>
                </Tooltip>
              ) : (
                <PlaceholderText>None</PlaceholderText>
              )}
            </AffiliationAttribute>
          </AffiliationAttributeContainer>
          <AddressAttributeContainer>
            <AddressAttribute>
              <KolAddress
                city={result.city}
                state={result.state}
                zip={result.zipCode}
                country={result.country}
                street={result.streetAddress} />
            </AddressAttribute>
          </AddressAttributeContainer>
          <EmailAttributeContainer>
            <EmailAttribute>
              {result.email ? (
                <Tooltip
                  title={result.email}
                  enterDelay={1000}
                  enterNextDelay={500}>
                  <EmailMailTo
                    href={buildKolMailTo({ email: result.email })}
                    target="_blank"
                    rel="noreferrer">
                    <EmailLinkText>{result.email}</EmailLinkText>
                    <StyledLinkIcon size={14} />
                  </EmailMailTo>
                </Tooltip>
              ) : (
                <PlaceholderText>None</PlaceholderText>
              )}
            </EmailAttribute>
          </EmailAttributeContainer>
          {showDistanceCol ?
            <PhoneAttributeContainer>
              <Attribute>
                {result.distance ? `${result.distance.toFixed(2)} mi` : 'N/A'}
              </Attribute>
            </PhoneAttributeContainer> :
            <PhoneAttributeContainer>
              <Attribute>
                {result.phone ? (
                  <EmailMailTo
                    href={createOneTapLink(result.phone)}
                    target="_blank"
                    rel="noreferrer">
                    {formatPhoneNumber(result.phone)}
                  </EmailMailTo>
                ) : (
                  <PlaceholderText>None</PlaceholderText>
                )}
              </Attribute>
            </PhoneAttributeContainer>
          }
        </FlexibleItemContainer>
      );
    });
  }, [records, isDemoEnv, columnWidths, allowSelection, selectedKols, showViewProfile, isInteractive, showDistanceCol, toggleKolSelection, onSelectProfile]);

  const fetchMoreData = useContext(FetchMoreResultsContext);

  const renderHeaderAttribute = useCallback((props: HeaderAttributeProps) => {
    return (
      <HeaderAttribute {...props} strokeColor={headerFilterStrokeColor} />
    );
  }, [headerFilterStrokeColor]);

  return (
    <>
      <Container className={className}>
        <HorizontalScrollContainer>
          <InnerContainer>
            <HeaderContainer ref={refs.setReference} {...getReferenceProps()} columns={columnWidths}>
              {allowSelection &&
                <CheckboxAttributeContainer>
                  <Attribute>Select</Attribute>
                </CheckboxAttributeContainer>
              }
              <NameAttributeContainer>
                {renderHeaderAttribute({
                  children: 'Name',
                  disabled: showFiltersInHeader,
                  sortKey: 'name',
                })}
              </NameAttributeContainer>
              {showViewProfile && (
                <ViewProfileAttributeContainer>
                  <Attribute>Profile</Attribute>
                </ViewProfileAttributeContainer>
              )}
              <AffiliationAttributeContainer>
                {renderHeaderAttribute({
                  children: 'Primary Affiliation',
                  disabled: showFiltersInHeader,
                  sortKey: 'affiliation',
                })}
              </AffiliationAttributeContainer>
              <AddressAttributeContainer>
                {renderHeaderAttribute({
                  children: 'Location',
                  disableSorting: true,
                  disabled: showFiltersInHeader,
                  sortKey: 'location',
                })}
              </AddressAttributeContainer>
              <EmailAttributeContainer>
                {renderHeaderAttribute({
                  children: 'Email',
                  disabled: showFiltersInHeader,
                  sortKey: 'email',
                })}
              </EmailAttributeContainer>

              {showDistanceCol ?
                <PhoneAttributeContainer>
                  {renderHeaderAttribute({
                    children: 'Distance',
                    disabled: showFiltersInHeader,
                    sortKey: 'distance',
                  })}
                </PhoneAttributeContainer> :
                <PhoneAttributeContainer>
                  {renderHeaderAttribute({
                    children: 'Phone',
                    disabled: showFiltersInHeader,
                    sortKey: 'phone',
                  })}
                </PhoneAttributeContainer>
              }
            </HeaderContainer>
            {!records.length && hasActiveFilters &&
              <NoResultsContainer>
                No results match your current filters or sorting. Please try adjusting your filters or sorting to see more results.
              </NoResultsContainer>}
            <StyledInfiniteScroll
              height={300}
              dataLength={records.length}
              next={fetchMoreData}
              hasMore={isInteractive && infiniteScroll && records.length < total}
              loader={<LoadingMessage>Loading...</LoadingMessage>}>
              {rows}
            </StyledInfiniteScroll>
          </InnerContainer>
        </HorizontalScrollContainer>
        {infiniteScroll
          ? <InfiniteScrollFooter showing={records.length} total={total} />
          : (
            <PaginationFooter
              pageIndex={pageIndex}
              pageSize={pageSize}
              setPageIndex={setPageIndex}
              total={total} />
          )
        }
      </Container>
      {filtersOpen &&
        <FilterPanelContainer ref={refs.setFloating} style={{ ...floatingStyles, top: filterPanelTopOffset }} {...getFloatingProps()}>
          <FilterPanel />
        </FilterPanelContainer>}
    </>
  );
};

const StyledInfiniteScroll = styled(InfiniteScroll)({
  backgroundColor: 'white',
  direction: 'rtl',

  '& > div': {
    direction: 'ltr',
  },
});

type PaginationFooterProps = {
  pageIndex: number;
  pageSize: number;
  setPageIndex: (pageIndex: number) => void;
  total: number;
};

const PaginationFooter = memo(({ pageIndex, pageSize, setPageIndex, total }: PaginationFooterProps) => {

  const {
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
  } = useTablePaginationHelpers({
    pageIndex,
    pageSize,
    setPageIndex,
    total,
  });

  if (total < pageSize) {
    return null;
  }

  return (
    <Footer>
      <TablePagination
        canNextPage={canNextPage}
        canPreviousPage={canPreviousPage}
        nextPage={nextPage}
        previousPage={previousPage}
        pageIndex={pageIndex}
        pageSize={pageSize}
        totalCount={total} />
    </Footer>
  );
});

type InfiniteScrollFooterProps = {
  showing: number;
  total: number;
};

const InfiniteScrollFooter = memo(({ showing, total }: InfiniteScrollFooterProps) => {

  return (
    <Footer>
      <FooterText>
        Showing {showing} of {total} results
      </FooterText>
    </Footer>
  );
});

const LoadingMessage = styled.div({
  padding: 8,
});

const FooterText = styled.div(({ theme }) => ({
  color: theme.palette.black.light1,
}));

const Badges = styled.div({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 5,
});

const Container = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  border: `2px solid ${theme.palette.gray.light1}`,
  borderRadius: 4,
  margin: 0,
  padding: 0,
}));

const HorizontalScrollContainer = styled.div({
  overflowX: 'auto',
  display: 'flex',
  flexDirection: 'column',
  height: '100%',
});

const InnerContainer = styled.div(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: 'fit-content',
  backgroundColor: theme.palette.gray.light2,
}));

type ColumnsProps = { columns: number[] };
const FlexibleItemContainer = styled.div<ColumnsProps>(({ columns }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: columns.map(c => `${c}px`).join(' '),
  width: '100%',
}));

const AttributeContainer = styled.div({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(var(--column-width-min), 1fr))',
});

const HeaderContainer = styled(FlexibleItemContainer)(({ columns, theme }) => ({
  boxSizing: 'border-box',
  display: 'grid',
  gridTemplateColumns: columns.map(c => `${c}px`).join(' '),
  flexShrink: 0,

  [`& > *`]: {
    backgroundColor: theme.palette.gray.light2,
    fontFamily: theme.fonts.semiBold,
    fontSize: 14,
  },
}));

const Attribute = styled.div(({ theme }) => ({
  boxSizing: 'border-box',
  borderRight: `1px solid ${theme.palette.gray.light2}`,
  borderBottom: `1px solid ${theme.palette.gray.light2}`,
  padding: 8,
  fontSize: 14,
  overflow: 'hidden',
  height: '100%',
}));

const StyledLinkIcon = styled(ExternalLink)(({ theme }) => ({
  color: theme.palette.hyperlink,
  transition: 'all 150ms',
  flexShrink: 0,
  marginLeft: 4,
}));

const CheckboxAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 60,
});

const NameAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 150,
});

const AffiliationAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 190,
});

const AddressAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 190,
});

const EmailAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 175,
});

const PhoneAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 150,
});

const ViewProfileAttributeContainer = styled(AttributeContainer)({
  ['--column-width-min']: 75,
});

const ViewProfileAttribute = styled(Attribute)({
  display: 'flex',
  justifyContent: 'center',
});

const SelectAttribute = styled(Attribute)({
  display: 'flex',
  justifyContent: 'center',
});

const StyledViewProfileButton = styled(TextQueryHint)({
  minWidth: 75,
  maxWidth: 75,
});

const NameAttribute = styled(Attribute)({

});

const AffiliationAttribute = styled(Attribute)({
  display: 'flex',
  overflow: 'hidden',
  // height: '3.7em',
});

const AddressAttribute = styled(Attribute)({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
  // height: '3.7em',
});

const EmailAttribute = styled(Attribute)({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
  // height: '3.7em',
});

const SortableAttribute = styled(Attribute)({
  cursor: 'pointer',
  display: 'flex',
  gap: 5,
  userSelect: 'none',
});

const EmailMailTo = styled.a(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  transition: 'all 150ms',
  overflow: 'hidden',
  height: '100%',

  ':hover': {
    color: theme.palette.hyperlink,
  },

  [`> ${String(StyledLinkIcon)}`]: {
    opacity: 0,
  },

  [`:hover > ${String(StyledLinkIcon)}`]: {
    opacity: 1,
  },
}));

const EmailLinkText = styled.div({
  height: '100%',
  wordBreak: 'break-all',
});

const Footer = styled.div({
  display: 'flex',
  justifyContent: 'flex-end',
  alignItems: 'center',
  paddingRight: 10,
  height: 34,
  boxSizing: 'border-box',
});

const PlaceholderText = styled.div(({ theme }) => ({
  color: theme.palette.gray.main,
}));

const FilterPanelContainer = styled.div({
  padding: 10,
  backgroundColor: 'white',
  transition: `box-shadow 300ms cubic-bezier(0.4, 0, 0.2, 1)`,
  borderRadius: 4,
  boxShadow: `0px 5px 5px -3px rgba(0, 0, 0, 0.2), 0px 8px 10px 1px rgba(0, 0, 0, 0.14), 0px 3px 14px 2px rgba(0, 0, 0, 0.12)`,
});

const NoResultsContainer = styled.div({
  width: '100%',
  textAlign: 'center',
  padding: 20,
  fontStyle: 'italic',
});

const SortIcon = (props: { sortDir: 'asc' | 'desc' }) => {
  if (props.sortDir === 'asc') {
    return <ArrowUp size={16} />;
  } else if (props.sortDir === 'desc') {
    return <ArrowDown size={16} />;
  } else {
    return null;
  }
};

const HeaderAttribute = (props: HeaderAttributeProps) => {
  const sortCtx = useContext(KolSearchResultsSortContext);
  const { setOpen, state, dispatch } = useContext(KolSearchResultsFilterContext);

  const activeFilters = state.filter(f => isFilterValid(f) && areFiltersSimilar(props.sortKey, f.key));
  const hasActiveFilter = activeFilters.length > 0;

  const onFilterClick = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    if (!state.some(f => f.key === props.sortKey)) {
      dispatch({
        type: 'filters-set',
        payload: [{
          key: props.sortKey,
          identifier: createId(),
          type: null,
          value: null,
        }],
      });
    }
    setOpen(old => !old);
  }, [dispatch, props.sortKey, setOpen, state]);

  if (props.disabled) {
    return (
      <Attribute style={{ display: 'flex', gap: 5 }}>
        {props.children}
      </Attribute>
    );
  }

  const renderFilterIcon = () => {
    return (
      <>
        <Filter size={18} stroke={hasActiveFilter ? props.strokeColor : 'black'} onClick={onFilterClick} />
        {activeFilters.length > 0 && <TableFilterTooltip activeFilters={activeFilters} />}
      </>
    );
  };

  if (props.disableSorting) {
    return (
      <Attribute style={{ display: 'flex', gap: 5 }}>
        {props.children}
        <div style={{ flexGrow: 1 }} />
        {renderFilterIcon()}
      </Attribute>
    );
  }

  return (
    <SortableAttribute onClick={() => sortCtx.toggleSortKey(props.sortKey)}>
      <div style={{ flexShrink: 0, flexWrap: 'nowrap', display: 'flex' }}>{props.children}</div>
      <div style={{ flexGrow: 1 }} />
      {sortCtx.sortBy === props.sortKey && <SortIcon sortDir={sortCtx.sortDir} />}
      {renderFilterIcon()}
    </SortableAttribute>
  );
};

type SortKey = KolSearchResultSortKey;

type HeaderAttributeProps = {
  sortKey: SortKey;
  disabled: boolean;
  disableSorting?: boolean;
  strokeColor?: string;
} & ChildrenProps;

const sortFns: SortFnMap<KolSearchResult, SortKey> = {
  email: (a, b) => a.email?.localeCompare(b.email),
  name: (a, b) => a.name?.localeCompare(b.name),
  firstName: (a, b) => a.firstName?.localeCompare(b.firstName),
  lastName: (a, b) => a.lastName?.localeCompare(b.lastName),
  phone: (a, b) => a.phone?.localeCompare(b.phone),
  location: (a, b) => a.streetAddress?.localeCompare(b.streetAddress),
  affiliation: (a, b) => a.affiliations[0]?.name?.localeCompare(b.affiliations[0]?.name),
  isMun: (a, b) => a.recommendations?.length - b.recommendations.length,
  distance: (a, b) => a.distance ?? Number.MAX_SAFE_INTEGER - (b.distance ?? Number.MAX_SAFE_INTEGER),
};

const accessorFnMap: StringAccessorFnMap<KolSearchResult, SortKey> = {
  email: x => x.email,
  name: x => x.name,
  firstName: x => x.firstName,
  lastName: x => x.lastName,
  phone: x => x.phone,
  location: x => [x.streetAddress, x.city, x.state, x.country, x.zipCode].filter(Boolean).join(' '),
  affiliation: x => x.affiliations[0]?.name,
  isMun: x => (!!x.recommendations?.filter(r => r.unmetNeeds.length > 0).length).toString(),
  distance: x => x.distance?.toString(),
};